import { StageType, Currency } from "../../../graphql/types";
import type {
  ParsedRoiScenario,
  ParsedRoiStage,
  RoiCalculationDataParsed,
} from "../hooks/useRoiCalculationData";
import type { RoiFarmGateInputs } from "../models/RoiCalculator";

export const DEFAULT_CURRENCY = "EUR";

export const ROI_CURRENCIES: readonly Currency[] = [
  Currency.Eur,
  Currency.Usd,
  Currency.Aud,
  Currency.Gbp,
  Currency.Brl,
];
export type RoiCurrency = Currency;

export const parseAwsJson = <T>(raw: unknown, maxUnwraps = 3): T | null => {
  let value = raw;
  for (let attempt = 0; attempt < maxUnwraps; attempt += 1) {
    if (value && typeof value === "object") return value as T;
    if (typeof value !== "string") return null;
    try {
      value = JSON.parse(value);
    } catch {
      return null;
    }
  }
  return value && typeof value === "object" ? (value as T) : null;
};

export const readNumber = (raw: unknown): number | undefined => {
  if (typeof raw === "number") return Number.isFinite(raw) ? raw : undefined;
  if (typeof raw === "string") {
    const n = Number(raw);
    return Number.isFinite(n) ? n : undefined;
  }
  if (raw && typeof raw === "object" && "value" in raw) {
    return readNumber((raw as { value: unknown }).value);
  }
  return undefined;
};

const normalizeType = (type: string | null): string =>
  (type ?? "").toUpperCase();

export const normalizeStageType = (type: string | null): string =>
  normalizeType(type);

const STAGE_OUTPUT_TYPE_LABEL_ID: Partial<Record<StageType, string>> = {
  [StageType.Breeding]:
    "SUSTELL.ROI.RESULTS.OUTPUT_TYPE.SPENT_HEN_ROOSTER_EGGS",
  [StageType.Growing]: "SUSTELL.ROI.RESULTS.OUTPUT_TYPE.KG_ANIMAL",
  [StageType.Fattening]: "SUSTELL.ROI.RESULTS.OUTPUT_TYPE.KG_ANIMAL",
  [StageType.Hatching]: "SUSTELL.ROI.RESULTS.OUTPUT_TYPE.DAY_OLD_CHICK",
  [StageType.Laying]: "SUSTELL.ROI.RESULTS.OUTPUT_TYPE.SPENT_HEN_EGGS",
  [StageType.Processing]: "SUSTELL.ROI.RESULTS.OUTPUT_TYPE.KG_FRESH_MEAT",
};

export const getStageOutputTypeLabelId = (type: string | null): string | null =>
  STAGE_OUTPUT_TYPE_LABEL_ID[normalizeStageType(type) as StageType] ?? null;

export const FARM_GATE_FIELD_MAP: Partial<
  Record<
    StageType,
    ReadonlyArray<{
      outputField: string;
      formField: keyof RoiFarmGateInputs;
    }>
  >
> = {
  [StageType.Growing]: [{ outputField: "price", formField: "liveWeightPrice" }],
  [StageType.Fattening]: [
    { outputField: "price", formField: "liveWeightPrice" },
  ],
  [StageType.ContinuousGrowing]: [
    { outputField: "price", formField: "liveWeightPrice" },
  ],
  [StageType.Breeding]: [
    { outputField: "price", formField: "eggSellingPrice" },
    { outputField: "spentHenPrice", formField: "spentHenRoosterPrice" },
    { outputField: "priceSoldPiglets", formField: "pigletsSellingPrice" },
    { outputField: "priceSoldPigs", formField: "spentSowSellingPrice" },
  ],
  [StageType.Laying]: [
    { outputField: "price", formField: "consumptionEggPrice" },
    { outputField: "spentHenPrice", formField: "spentHen" },
  ],
  [StageType.Hatching]: [
    { outputField: "price", formField: "dayOldChickPrice" },
  ],
};

export const findStageByType = (
  scenario: ParsedRoiScenario | null | undefined,
  types: readonly string[]
): ParsedRoiStage | undefined => {
  const stages = scenario?.stages;
  if (!stages) return undefined;
  // `types` is a priority list (highest-ranked first), not just a set of
  // acceptable matches — search stage-type by stage-type, in that order,
  // rather than scanning `stages` once and returning whichever wanted type
  // happens to appear first in the scenario's own stage order.
  return types.reduce<ParsedRoiStage | undefined>((found, type) => {
    if (found) return found;
    const wanted = type.toUpperCase();
    return stages.find((s) => normalizeType(s.stageType) === wanted);
  }, undefined);
};

export const hasProcessingStage = (
  scenario: ParsedRoiScenario | null | undefined
): boolean => Boolean(findStageByType(scenario, [StageType.Processing]));

export const hasHatchingStage = (
  scenario: ParsedRoiScenario | null | undefined
): boolean => Boolean(findStageByType(scenario, [StageType.Hatching]));

const readOutput = (stage?: ParsedRoiStage): Record<string, unknown> =>
  (stage?.stageData?.output as Record<string, unknown> | undefined) ?? {};

const readOutputPrice = (stage?: ParsedRoiStage): number | undefined =>
  readNumber(readOutput(stage).price);

const readAnimalsOutput = (stage?: ParsedRoiStage): Record<string, unknown> =>
  (stage?.stageData?.animalsOutput as Record<string, unknown> | undefined) ??
  {};

const isPigStage = (stage?: ParsedRoiStage): boolean =>
  Boolean(stage?.stageData && "animalsOutput" in stage.stageData);

export type RoiSpecies = "PIG" | "POULTRY";

export const getRoiSpecies = (
  scenario: ParsedRoiScenario | null | undefined
): RoiSpecies =>
  scenario?.stages?.some((s) => s && isPigStage(s)) ? "PIG" : "POULTRY";

export const getTerminalOutputStage = (
  scenario: ParsedRoiScenario | null | undefined
): ParsedRoiStage | undefined => {
  const primary = findStageByType(scenario, [
    StageType.Growing,
    StageType.Fattening,
    StageType.Laying,
    StageType.ContinuousGrowing,
    StageType.Hatching,
  ]);
  if (primary) return primary;
  // A breeding-only farm (poultry broiler-breeder or pig sow) sells at the
  // BREEDING stage itself — but only as a fallback, when none of the primary
  // types above exist. findStageByType now checks the primary list in
  // priority order (GROWING/FATTENING/LAYING/CONTINUOUS_GROWING outrank
  // HATCHING), so a fully integrated farm (e.g. Broiler with BREEDING +
  // HATCHING + GROWING + PROCESSING all present, allowed by allowedStageTypes
  // in animals.ts) still resolves to its actual grow-out/processing output,
  // never to the upstream breeding or hatching stage. Mirrors the same
  // GROWING/FATTENING-before-BREEDING/HATCHING priority
  // persist_intervention_roi.ts uses to route SciTell requests.
  return findStageByType(scenario, [StageType.Breeding]);
};

export const getTerminalOutputStageType = (
  scenario: ParsedRoiScenario | null | undefined
): string | null => getTerminalOutputStage(scenario)?.stageType ?? null;

const PRICE_FIELD = /price/i;

const pickPriceFields = (
  source: Record<string, unknown>
): Record<string, number> =>
  Object.entries(source).reduce<Record<string, number>>((acc, [key, raw]) => {
    if (!PRICE_FIELD.test(key)) return acc;
    const price = readNumber(raw);
    if (price !== undefined) acc[key] = price;
    return acc;
  }, {});

interface StagePriceReader {
  farmGatePrice: (stage?: ParsedRoiStage) => number | undefined;

  outputPrices: (stage?: ParsedRoiStage) => Record<string, number>;
}

const poultryPriceReader: StagePriceReader = {
  farmGatePrice: (stage) => readNumber(readOutput(stage).price),
  outputPrices: (stage) => pickPriceFields(readOutput(stage)),
};

const pigPriceReader: StagePriceReader = {
  farmGatePrice: (stage) =>
    readNumber(readAnimalsOutput(stage).priceSoldPigs) ??
    readNumber(stage?.stageData?.pricePerWeightPigsEnd),
  outputPrices: (stage) => pickPriceFields(readAnimalsOutput(stage)),
};

const SPECIES_PRICE_READERS: Record<RoiSpecies, StagePriceReader> = {
  POULTRY: poultryPriceReader,
  PIG: pigPriceReader,
};

const priceReaderForStage = (stage?: ParsedRoiStage): StagePriceReader =>
  isPigStage(stage) ? SPECIES_PRICE_READERS.PIG : SPECIES_PRICE_READERS.POULTRY;

export const getOutputPrices = (
  stage?: ParsedRoiStage
): Record<string, number> => priceReaderForStage(stage).outputPrices(stage);

export const getFarmGatePrice = (
  scenario: ParsedRoiScenario | null | undefined
): number | undefined => {
  const terminal = getTerminalOutputStage(scenario);
  return priceReaderForStage(terminal).farmGatePrice(terminal);
};

export const getDayOldChickenPrice = (
  scenario: ParsedRoiScenario | null | undefined
): number | undefined =>
  readOutputPrice(findStageByType(scenario, [StageType.Hatching]));

export interface PigBreedingPrices {
  pigletPrice?: number;
  cullSowPrice?: number;
}

export const getPigBreedingPrices = (
  scenario: ParsedRoiScenario | null | undefined
): PigBreedingPrices => {
  const output = readAnimalsOutput(
    findStageByType(scenario, [StageType.Breeding])
  );
  return {
    pigletPrice: readNumber(output.priceSoldPiglets),
    cullSowPrice: readNumber(output.priceSoldPigs),
  };
};

export const isFeedBearingStage = (stage: ParsedRoiStage): boolean => {
  const data = stage.stageData;
  return Boolean(
    data?.feedingProgram ||
      data?.henFeedingProgram ||
      data?.roosterFeedingProgram
  );
};

export const getFeedBearingStages = (
  scenario: ParsedRoiScenario | null | undefined
): ParsedRoiStage[] =>
  (scenario?.stages ?? []).filter((s) => s && isFeedBearingStage(s));

export interface ProcessingOutputItem {
  weight?: number;
  price?: number;
}

export const getProcessingOutputs = (
  scenario: ParsedRoiScenario | null | undefined
): Record<string, ProcessingOutputItem> => {
  const processing = findStageByType(scenario, [StageType.Processing]);
  const outputs = processing?.stageData?.processingOutput as
    | Record<string, unknown>
    | undefined;
  if (!outputs || typeof outputs !== "object") return {};

  return Object.entries(outputs).reduce<Record<string, ProcessingOutputItem>>(
    (acc, [category, entry]) => {
      const item = entry as { price?: unknown; weight?: unknown } | undefined;
      const price = readNumber(item?.price);
      const weight = readNumber(item?.weight);
      if (price !== undefined || weight !== undefined) {
        acc[category] = { price, weight };
      }
      return acc;
    },
    {}
  );
};

export const getProcessingOutputPrices = (
  scenario: ParsedRoiScenario | null | undefined
): Record<string, number> =>
  Object.fromEntries(
    Object.entries(getProcessingOutputs(scenario))
      .filter(([, item]) => item.price !== undefined)
      .map(([k, item]) => [k, item.price as number])
  );

export interface FarmGateState {
  editable: boolean;

  disabled: boolean;

  prefilled: boolean;

  value?: number;

  reason: "PROCESSING" | "HATCHING_EMPTY" | "BREEDING_EMPTY" | "DEFAULT";
}

export interface ResolveFarmGateArgs {
  baseline: ParsedRoiScenario | null | undefined;
}

export const resolveFarmGate = ({
  baseline,
}: ResolveFarmGateArgs): FarmGateState => {
  if (hasProcessingStage(baseline)) {
    return {
      editable: false,
      disabled: true,
      prefilled: false,
      reason: "PROCESSING",
    };
  }

  if (
    hasHatchingStage(baseline) &&
    getDayOldChickenPrice(baseline) === undefined
  ) {
    return {
      editable: true,
      disabled: false,
      prefilled: false,
      reason: "HATCHING_EMPTY",
    };
  }

  const breedingStage = findStageByType(baseline, [StageType.Breeding]);
  if (getRoiSpecies(baseline) === "PIG" && breedingStage) {
    const { pigletPrice, cullSowPrice } = getPigBreedingPrices(baseline);
    if (pigletPrice === undefined || cullSowPrice === undefined) {
      return {
        editable: true,
        disabled: false,
        prefilled: false,
        reason: "BREEDING_EMPTY",
      };
    }
  }

  const value = getFarmGatePrice(baseline);
  return {
    editable: true,
    disabled: false,
    prefilled: value !== undefined,
    value,
    reason: "DEFAULT",
  };
};

export interface RoiFormStageDescriptor {
  stageId: string | null;
  stageName: string | null;
  stageType: string | null;
}

export interface RoiFormData {
  currency: string;

  defaultCurrency: string;

  currencyOptions: readonly string[];

  isNonDefaultCurrency: boolean;

  feedBearingStages: RoiFormStageDescriptor[];

  farmGate: FarmGateState;

  terminalOutputStageType: string | null;

  farmGatePrices: {
    baseline: Record<string, number>;
    intervention: Record<string, number>;
  };

  processingOutputPrices: Record<string, number>;

  processingOutputs: {
    baseline: Record<string, ProcessingOutputItem>;
    intervention: Record<string, ProcessingOutputItem>;
  };
  hasProcessingStage: boolean;

  processingStageName: string | null;
  hasHatchingStage: boolean;

  species: RoiSpecies;

  dayOldChickenPrice?: number;
}

const toStageDescriptor = (stage: ParsedRoiStage): RoiFormStageDescriptor => ({
  stageId: stage.stageId,
  stageName: stage.stageName,
  stageType: stage.stageType,
});

export const buildRoiFormData = (
  data: RoiCalculationDataParsed | null | undefined,
  selectedCurrency?: string | null
): RoiFormData => {
  const defaultCurrency = data?.defaultCurrency ?? DEFAULT_CURRENCY;
  const currency = selectedCurrency ?? defaultCurrency;
  const baseline = data?.baseline;
  const intervention = data?.intervention;

  const isNonDefaultCurrency =
    currency.toUpperCase() !== defaultCurrency.toUpperCase();

  return {
    currency,
    defaultCurrency,
    currencyOptions: ROI_CURRENCIES,
    isNonDefaultCurrency,
    feedBearingStages: getFeedBearingStages(baseline).map(toStageDescriptor),
    farmGate: resolveFarmGate({ baseline }),
    terminalOutputStageType: getTerminalOutputStageType(baseline),
    farmGatePrices: {
      baseline: getOutputPrices(getTerminalOutputStage(baseline)),
      intervention: getOutputPrices(getTerminalOutputStage(intervention)),
    },
    processingOutputPrices: getProcessingOutputPrices(baseline),
    processingOutputs: (() => {
      const baselineOutputs = getProcessingOutputs(baseline);
      const interventionOutputs = getProcessingOutputs(intervention);

      return {
        baseline: baselineOutputs,
        intervention:
          Object.keys(interventionOutputs).length > 0
            ? interventionOutputs
            : baselineOutputs,
      };
    })(),
    hasProcessingStage: hasProcessingStage(baseline),
    processingStageName:
      findStageByType(baseline, [StageType.Processing])?.stageName ?? null,
    hasHatchingStage: hasHatchingStage(baseline),
    species: getRoiSpecies(baseline),
    dayOldChickenPrice: getDayOldChickenPrice(baseline),
  };
};
